import React, { useState, useRef, useEffect } from "react";
import bodhibotlogo from "../assets/images/Bodhibotlogo.webp";

const ICON_SIZE = 60;
const MARGIN = 20;
const CHATBOX_MIN_WIDTH = 380;
const CHATBOX_MIN_HEIGHT = 490;
const CHATBOX_MAX_WIDTH = 500;
const CHATBOX_MAX_HEIGHT = 600;

export const ChatWindow = ({
  children,
  isEnabled = true,
  title = "BodhiBot",
  options = [],
  onTitleChange = null,
}) => {
  console.log("Chatwindow rendered!", { isEnabled })
  const [isOpen, setIsOpen] = useState(false);
  const [iconPos, setIconPos] = useState({
    left: window.innerWidth - ICON_SIZE - MARGIN,
    top: window.innerHeight - ICON_SIZE - MARGIN,
  });
  const [windowProps, setWindowProps] = useState({
    left: window.innerWidth - CHATBOX_MIN_WIDTH - MARGIN,
    top: window.innerHeight - CHATBOX_MIN_HEIGHT - MARGIN,
    height: CHATBOX_MIN_HEIGHT,
    width: CHATBOX_MIN_WIDTH,
  });

  const [dragging, setDragging] = useState(false);
  const [dragMoved, setDragMoved] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [iconChatOffset, setIconChatOffset] = useState({ x: 0, y: 0 });

  const startPos = useRef({ x: 0, y: 0 });
  const iconRef = useRef(null);
  const chatboxRef = useRef(null);
  const resizeDir = useRef(null);

  const ICON_MAX_X = window.innerWidth - ICON_SIZE - MARGIN;
  const ICON_MAX_Y = window.innerHeight - ICON_SIZE - MARGIN;

  const handleMouseDown = (e, isChatbox = false) => {
    const rect = isChatbox
      ? chatboxRef.current.getBoundingClientRect()
      : iconRef.current.getBoundingClientRect();
    setOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    startPos.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
  };

  const handleToggle = () => {
    if (dragMoved) return;

    if (!isOpen) {
      const spaceRight = window.innerWidth - (iconPos.left + ICON_SIZE);
      const spaceBottom = window.innerHeight - (iconPos.top + ICON_SIZE);

      let left, top;

      if (spaceRight >= windowProps.width + MARGIN) {
        left = iconPos.left;
      } else if (iconPos.left >= windowProps.width + MARGIN) {
        left = iconPos.left + ICON_SIZE - windowProps.width;
      } else {
        left = Math.min(
          Math.max(iconPos.left - windowProps.width / 2, MARGIN),
          window.innerWidth - windowProps.width - MARGIN
        );
      }

      if (spaceBottom >= windowProps.height + MARGIN) {
        top = iconPos.top;
      } else if (iconPos.top >= windowProps.height + MARGIN) {
        top = iconPos.top + ICON_SIZE - windowProps.height;
      } else {
        top = Math.min(
          Math.max(iconPos.top - windowProps.height / 2, MARGIN),
          window.innerHeight - windowProps.height - MARGIN
        );
      }

      setWindowProps((prev) => ({ ...prev, left, top }));
      setIconChatOffset({
        x: iconPos.left - left,
        y: iconPos.top - top,
      });
    }

    setIsOpen((prev) => !prev);
  };

  // Dragging movement listener
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging) return;

      const dx = Math.abs(e.clientX - startPos.current.x);
      const dy = Math.abs(e.clientY - startPos.current.y);
      if (dx > 3 || dy > 3) setDragMoved(true);

      document.body.style.userSelect = "none";
      const CHATBOX_MAX_X = window.innerWidth - windowProps.width - MARGIN;
      const CHATBOX_MAX_Y = window.innerHeight - windowProps.height - MARGIN;

      if (isOpen) {
        const newLeft = Math.min(Math.max(e.clientX - offset.x, MARGIN), CHATBOX_MAX_X);
        const newTop = Math.min(Math.max(e.clientY - offset.y, MARGIN), CHATBOX_MAX_Y);
        setWindowProps((prev) => ({ ...prev, left: newLeft, top: newTop }));
        setIconPos({
          left: newLeft + iconChatOffset.x,
          top: newTop + iconChatOffset.y,
        });
      } else {
        const newLeft = Math.min(Math.max(e.clientX - offset.x, MARGIN), ICON_MAX_X);
        const newTop = Math.min(Math.max(e.clientY - offset.y, MARGIN), ICON_MAX_Y);
        setIconPos({ left: newLeft, top: newTop });
      }
    };

    const handleMouseUp = () => {
      setDragging(false);
      setTimeout(() => setDragMoved(false), 50);
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, offset, isOpen, windowProps.width, windowProps.height, iconChatOffset]);

  // Resizing listener
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizeDir.current) return;
      e.preventDefault();

      setWindowProps((prev) => {
        let newWidth = prev.width;
        let newHeight = prev.height;
        let newLeft = prev.left;
        let newTop = prev.top;
        let iconLeft = iconPos.left;
        let iconTop = iconPos.top;
        let isChanged = false;

        switch (resizeDir.current) {
          case "top-left":
            if (e.clientX >= MARGIN) {
              newWidth = Math.min(
                Math.max(prev.width - (e.clientX - prev.left), CHATBOX_MIN_WIDTH),
                CHATBOX_MAX_WIDTH
              );
              newLeft = prev.left + (prev.width - newWidth);
            }
            if (e.clientY >= MARGIN) {
              newHeight = Math.min(
                Math.max(prev.height - (e.clientY - prev.top), CHATBOX_MIN_HEIGHT),
                CHATBOX_MAX_HEIGHT
              );
              newTop = prev.top + (prev.height - newHeight);
            }
            if (newLeft > iconLeft) { iconLeft = newLeft; isChanged = true; }
            if (newTop > iconTop) { iconTop = newTop; isChanged = true; }
            break;

          case "top-right":
            if (e.clientX <= window.innerWidth - MARGIN) {
              newWidth = Math.min(
                Math.max(e.clientX - prev.left, CHATBOX_MIN_WIDTH),
                CHATBOX_MAX_WIDTH
              );
            }
            if (e.clientY >= MARGIN) {
              newHeight = Math.min(
                Math.max(prev.height - (e.clientY - prev.top), CHATBOX_MIN_HEIGHT),
                CHATBOX_MAX_HEIGHT
              );
              newTop = prev.top + (prev.height - newHeight);
            }
            if (newLeft + newWidth < iconLeft + ICON_SIZE) {
              iconLeft = newLeft + newWidth - ICON_SIZE;
              isChanged = true;
            }
            if (newTop > iconTop) { iconTop = newTop; isChanged = true; }
            break;

          case "bottom-left":
            if (e.clientX >= MARGIN) {
              newWidth = Math.min(
                Math.max(prev.width - (e.clientX - prev.left), CHATBOX_MIN_WIDTH),
                CHATBOX_MAX_WIDTH
              );
              newLeft = prev.left + (prev.width - newWidth);
            }
            if (e.clientY <= window.innerHeight - MARGIN) {
              newHeight = Math.min(
                Math.max(e.clientY - prev.top, CHATBOX_MIN_HEIGHT),
                CHATBOX_MAX_HEIGHT
              );
            }
            if (newLeft > iconLeft) { iconLeft = newLeft; isChanged = true; }
            if (newTop + newHeight < iconTop + ICON_SIZE) {
              iconTop = newTop + newHeight - ICON_SIZE;
              isChanged = true;
            }
            break;

          case "bottom-right":
            if (e.clientX <= window.innerWidth - MARGIN) {
              newWidth = Math.min(
                Math.max(e.clientX - prev.left, CHATBOX_MIN_WIDTH),
                CHATBOX_MAX_WIDTH
              );
            }
            if (e.clientY <= window.innerHeight - MARGIN) {
              newHeight = Math.min(
                Math.max(e.clientY - prev.top, CHATBOX_MIN_HEIGHT),
                CHATBOX_MAX_HEIGHT
              );
            }
            if (newLeft + newWidth < iconLeft + ICON_SIZE) {
              iconLeft = newLeft + newWidth - ICON_SIZE;
              isChanged = true;
            }
            if (newTop + newHeight < iconTop + ICON_SIZE) {
              iconTop = newTop + newHeight - ICON_SIZE;
              isChanged = true;
            }
            break;
        }

        if (isChanged) setIconPos({ left: iconLeft, top: iconTop });
        return { width: newWidth, height: newHeight, left: newLeft, top: newTop };
      });
    };

    const handleMouseUp = () => {
      resizeDir.current = null;
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [iconPos]);

  if (!isEnabled) return null;

  return (
    <div key="bodhibot">
      {!isOpen ? (
        <img
          ref={iconRef}
          src={bodhibotlogo}
          alt="Bodhibot"
          style={{
            position: "fixed",
            left: iconPos.left,
            top: iconPos.top,
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: "50%",
            cursor: "grab",
            userSelect: "none",
            border: "2px solid rgba(255,255,255,0.8)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25), 0 0 8px rgba(0,123,255,0.3)",
            backgroundColor: "white",
            zIndex: 1200,
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseDown={(e) => handleMouseDown(e)}
          onClick={handleToggle}
          draggable={false}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow =
              "0 6px 16px rgba(0,0,0,0.3), 0 0 10px rgba(0,123,255,0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(0,0,0,0.25), 0 0 8px rgba(0,123,255,0.3)";
          }}
        />
      ) : (
        <div
          ref={chatboxRef}
          style={{
            position: "fixed",
            left: windowProps.left,
            top: windowProps.top,
            width: windowProps.width,
            height: windowProps.height,
            backgroundColor: "#ffffff",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            zIndex: 1200,
            overflow: "hidden",
            transition: "box-shadow 0.3s ease",
            minWidth: CHATBOX_MIN_WIDTH,
            minHeight: CHATBOX_MIN_HEIGHT,
            maxWidth: CHATBOX_MAX_WIDTH,
            maxHeight: CHATBOX_MAX_HEIGHT,
            boxShadow: dragging
              ? "0 0 0 2px rgba(0,123,255,0.2)"
              : "0 8px 24px rgba(0,0,0,0.15)",
            cursor: dragging ? "grabbing" : "default",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#007bff",
              color: "white",
              padding: "10px 14px",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "grab",
              height: "55px",
              fontSize: "20px",
              fontWeight: "550",
              letterSpacing: "0.5px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
            onMouseDown={(e) => handleMouseDown(e, true)}
          >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                }}
                >
                {options.length > 0 && onTitleChange ? (
                    <select
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    style={{
                        background: "transparent",
                        color: "white",
                        border: "none",
                        outline: "none",
                        fontSize: "20px",
                        fontWeight: "550",
                        cursor: "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        paddingRight: "18px",
                    }}
                    >
                    {options.map((option) => (
                        <option
                        key={option}
                        value={option}
                        style={{
                            color: "black",
                        }}
                        >
                        {option}
                        </option>
                    ))}
                    </select>
                ) : (
                    <span>{title}</span>
                )}

                {options.length > 0 && (
                    <span
                    style={{
                        marginLeft: "-14px",
                        pointerEvents: "none",
                        fontSize: "12px",
                    }}
                    >
                    ▼
                    </span>
                )}
                </div>
            <button
              onClick={handleToggle}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                fontSize: "18px",
                cursor: "pointer",
                fontWeight: "bold",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              ✕
            </button>
          </div>

          {/* Child Content (ChatBot logic) */}
          {typeof children === "function" ? children({ isOpen, setIsOpen }) : children}

          {/* Resize Corner Handles */}
          {["top-left", "top-right", "bottom-left", "bottom-right"].map((pos) => (
            <div
              key={pos}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                resizeDir.current = pos;
                document.body.style.userSelect = "none";
              }}
              style={{
                position: "absolute",
                width: "12px",
                height: "12px",
                zIndex: 1200,
                background: "transparent",
                ...(pos === "top-left" && { top: 0, left: 0, cursor: "nwse-resize" }),
                ...(pos === "top-right" && { top: 0, right: 0, cursor: "nesw-resize" }),
                ...(pos === "bottom-left" && { bottom: 0, left: 0, cursor: "nesw-resize" }),
                ...(pos === "bottom-right" && { bottom: 0, right: 0, cursor: "nwse-resize" }),
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};